import { inject, Injectable } from '@angular/core';
import { ApiDataAccess, ApiResourceLocation, Dictionary } from '@softline/core';
import {
  SOFTLINE_API_GARAGE_MECHANIC_TIME_REPORT_ORDER,
  SOFTLINE_API_GARAGE_MECHANIC_TIME_REPORT_STOP,
  SOFTLINE_API_GARAGE_MECHANIC_TIME_REPORT_STOP_ALL,
  SOFTLINE_API_GARAGE_MECHANIC_TIME_REPORTS,
  SOFTLINE_API_GARAGE_ORDER_TIME_REPORTS,
  SOFTLINE_API_GARAGE_TIME_REPORT,
  SOFTLINE_API_GARAGE_TIME_REPORT_ID,
} from '../garage.api';
import { GarageTask } from '../models/garage-task';
import { TimeReport, TimeReportFilter, TimeReportInput } from '../models/time-report';

@Injectable()
export class TimeReportRepository {
  readonly #dataAccess = inject(ApiDataAccess);

  private createLocation(
    path: string,
    pathParams?: Dictionary<unknown> | null,
    queryParams?: Dictionary<unknown> | null
  ): ApiResourceLocation {
    const location: ApiResourceLocation = { path };
    if (pathParams) {
      location.pathParams = pathParams;
    }
    if (queryParams) {
      location.queryParams = queryParams;
    }
    return location;
  }
  getTimeReportsByMechanicId(id: number, date): Promise<TimeReport[]> {
    return this.#dataAccess.get(
      this.createLocation(
        SOFTLINE_API_GARAGE_MECHANIC_TIME_REPORTS,
        { id },
        { from: date.split('T')[0], to: date.split('T')[0] }
      )
    );
  }

  // Unproduktive stamp
  startTimeReportByMechanicId(id: number, task: GarageTask): Promise<TimeReport> {
    return this.#dataAccess.create(
      this.createLocation(SOFTLINE_API_GARAGE_MECHANIC_TIME_REPORTS, { id }),
      task
    );
  }

  startTimeReportByOrderIdAndMechanicId(
    orderId: number,
    mechanicId: number,
    task: GarageTask
  ): Promise<TimeReport> {
    return this.#dataAccess.create(
      this.createLocation(SOFTLINE_API_GARAGE_MECHANIC_TIME_REPORT_ORDER, {
        orderId,
        mechanicId,
      }),
      task
    );
  }

  stopTimeReportByMechanicId(id: number): Promise<TimeReport> {
    return this.#dataAccess.create(
      this.createLocation(SOFTLINE_API_GARAGE_MECHANIC_TIME_REPORT_STOP, { id }),
      null
    );
  }

  getTimeReportsByOrderId(id: number): Promise<TimeReport[]> {
    return this.#dataAccess.get(
      this.createLocation(SOFTLINE_API_GARAGE_ORDER_TIME_REPORTS, { id })
    );
  }

  //* Time Report CRUD (explicit from/to entries — GET/POST/PUT/DELETE) *//

  /** GET /garage/time-report — list with optional filters. */
  getTimeReports(filter: TimeReportFilter = {}): Promise<TimeReport[]> {
    return this.#dataAccess.get(
      this.createLocation(SOFTLINE_API_GARAGE_TIME_REPORT, null, {
        userId: filter.userId,
        orderId: filter.orderId,
        from: filter.from,
        to: filter.to,
        onlyActive: filter.onlyActive || undefined,
      })
    );
  }

  /** POST /garage/time-report — create an explicit from/to time report. */
  createTimeReport(input: TimeReportInput): Promise<TimeReport> {
    return this.#dataAccess.create(
      this.createLocation(SOFTLINE_API_GARAGE_TIME_REPORT),
      input
    );
  }

  /** PUT /garage/time-report/{id} — partial update (only provided fields). */
  updateTimeReport(id: number, input: TimeReportInput): Promise<TimeReport> {
    return this.#dataAccess.update(
      this.createLocation(SOFTLINE_API_GARAGE_TIME_REPORT_ID, { id }),
      input
    );
  }

  /** DELETE /garage/time-report/{id} — delete; returns the deleted record. */
  deleteTimeReportById(id: number): Promise<TimeReport> {
    return this.#dataAccess.delete(
      this.createLocation(SOFTLINE_API_GARAGE_TIME_REPORT_ID, { id })
    );
  }

  /** POST /garage/mechanics/{id}/time-report/stop-all — stop all running
      reports for a mechanic (Lstfix id). Returns the stopped reports. */
  stopAllTimeReportsByMechanicId(id: number): Promise<TimeReport[]> {
    return this.#dataAccess.create(
      this.createLocation(SOFTLINE_API_GARAGE_MECHANIC_TIME_REPORT_STOP_ALL, { id }),
      null
    );
  }
}
